/* A TOUR THAT POINTS AT THE REAL SCREEN — Max, 2026-09-24, from Jay's mail of 2026-09-16: "the
   best system is one that doesn't need a manual — where the interface itself makes the right path
   obvious. We have a manual, but I'd rather the tool guide them naturally."

   A slideshow of screenshots is a manual with a play button, so this is not one. Every step points
   at a control that is ON THE PAGE, lights it, and says what it is for; there is nothing to read
   that cannot be clicked the moment the tour ends. That also makes it self-maintaining in the one
   way that matters: a step whose control no longer exists is SKIPPED rather than shown against
   empty space, so a button that gets renamed or removed cannot leave the tour describing a screen
   nobody has.

   The spotlight is one box with a huge outer shadow — the classic hole-in-the-overlay — rather than
   four rectangles around the target, because four rectangles have three seams and they show.

   It re-measures on scroll and resize: a tour that points 200px above the button after somebody
   nudges the wheel is worse than no tour. */
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './TourGuide.css';

const PAD = 8;           // breathing room around the lit control
const GAP = 12;          // between the spotlight and the card
const MARGIN = 12;       // the card never comes closer than this to the window edge

export default function TourGuide({ steps, open, onClose, onFinish }) {
  const { t } = useTranslation();
  const [i, setI] = useState(0);
  const [box, setBox] = useState(null);
  const cardRef = useRef(null);
  const [cardPos, setCardPos] = useState({ top: 0, left: 0 });

  /* Only the steps whose control is actually on this screen — measured AFTER a paint, into state,
     not during a render. The first version computed this in a useMemo while rendering, which is a
     race it lost every time: the tour opens itself shortly after the screen does, and a control
     that depends on data still in flight (the trial-sample button waits on the tracker config) was
     simply not in the DOM yet. The memo never ran again, so the step was gone for good and the
     tour silently had six steps instead of seven. */
  const [live, setLive] = useState([]);
  useLayoutEffect(() => {
    if (!open) { setLive([]); return undefined; }
    /* A step with `prepare` is kept whatever the DOM holds at open: prepare() switches the screen (a tab) and
       the control appears after it — Sales Data Load walks its whole flow across five tabs this way. */
    const read = () => setLive(steps.filter((s) => !s.selector || s.prepare || document.querySelector(s.selector)));
    read();
    // And once more after the next tick, for anything that lands between the click and the paint.
    const id = window.setTimeout(read, 120);
    return () => window.clearTimeout(id);
  }, [open, steps]);
  const step = live[i];

  const place = useCallback(() => {
    if (!step) return;
    const el = step.selector ? document.querySelector(step.selector) : null;
    if (!el) { setBox(null); return; }
    const r = el.getBoundingClientRect();
    /* CLAMPED TO THE WINDOW. A table row is as wide as its table, and this table scrolls sideways —
       a row measured 2973px across a 1700px screen, so the spotlight ran off both the page and any
       sense of what it was pointing at. Lighting the visible part of a control that is wider than
       the screen is the honest answer: that IS what the reader can see. */
    const top = Math.max(0, r.top - PAD);
    const left = Math.max(0, r.left - PAD);
    let height = Math.max(0, Math.min(r.bottom + PAD, window.innerHeight) - top);
    /* A CONTROL TALLER THAN THE ROOM THE CARD NEEDS — Submit Cash Report's entry form, 2026-10-15,
       and the 13-week plan worse than the weekly form. With the box filling the window the card had
       nowhere to go but on top of the thing it describes. Only when the card fits neither below nor
       above is the box shortened, to end where the card can start: the top of the control is what
       the step is about, and every step that already fitted is lit exactly as before. */
    const cardH = cardRef.current ? cardRef.current.getBoundingClientRect().height : 220;
    const H = window.innerHeight;
    if (top + height + GAP + cardH > H - MARGIN && top - GAP - cardH < MARGIN) {
      height = Math.max(48, H - MARGIN - cardH - GAP - top);
    }
    setBox({
      top,
      left,
      width: Math.max(0, Math.min(r.right + PAD, window.innerWidth) - left),
      height,
    });
  }, [step]);

  useEffect(() => { if (open) setI(0); }, [open]);

  useLayoutEffect(() => {
    if (!open || !step) return undefined;
    if (step.prepare) step.prepare();
    let waitId = null;
    let tries = 0;
    const go = () => {
    const el = step.selector ? document.querySelector(step.selector) : null;
    /* After prepare() the control is still being drawn: wait for it (up to ~1.5 s) before scrolling and
       measuring, or the box lights the place it will be rather than where it is. */
    if (!el && step.prepare && tries++ < 18) { waitId = window.setTimeout(go, 80); return; }
    // Bring it into view FIRST, then measure — measuring before the scroll settles lights the
    // place the control used to be.
    if (el) {
      /* Centred, unless it is taller than half the window: then its TOP is brought just under the
         portal's header, which is fixed over the scroller — measured, not declared, because the
         header has no height of its own to read. Centring a tall control hid where it begins. */
      const tall = el.getBoundingClientRect().height > window.innerHeight * 0.5;
      if (tall) {
        const head = document.querySelector('.header-container');
        const prev = el.style.scrollMarginTop;
        el.style.scrollMarginTop = `${Math.round((head ? head.getBoundingClientRect().bottom : 0) + PAD + GAP)}px`;
        el.scrollIntoView({ block: 'start', inline: 'nearest', behavior: 'auto' });
        el.style.scrollMarginTop = prev;
      } else {
        el.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'auto' });
      }
    }
    waitId = window.setTimeout(place, 60);
    };
    go();
    /* A prepared step's control can be REPLACED after it was found — two steps on one selector on two tabs (Sales
       Data Load's New customers → New SKUs): the old tab's control answers at once, then the new tab draws its own
       once its data is in. Measure again a few times, so the box ends on the control that is actually there. */
    const again = step.prepare ? [300, 800, 1500].map((ms) => window.setTimeout(place, ms)) : [];
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.clearTimeout(waitId);
      again.forEach((id) => window.clearTimeout(id));
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, step, place]);

  /* The card is placed after it has been drawn, because where it fits depends on how tall it is —
     and it is taller in Korean than in English. Below the spotlight normally; above when there is
     no room below; clamped into the window either way. */
  useLayoutEffect(() => {
    if (!open || !cardRef.current) return;
    const c = cardRef.current.getBoundingClientRect();
    const W = window.innerWidth;
    const H = window.innerHeight;
    if (!box) {
      setCardPos({ top: Math.round((H - c.height) / 2), left: Math.round((W - c.width) / 2) });
      return;
    }
    const below = box.top + box.height + GAP;
    const above = box.top - GAP - c.height;
    const top = below + c.height <= H - MARGIN ? below
      : above >= MARGIN ? above
        : Math.max(MARGIN, Math.min(H - c.height - MARGIN, below));
    const left = Math.max(MARGIN, Math.min(W - c.width - MARGIN,
      box.left + box.width / 2 - c.width / 2));
    setCardPos({ top: Math.round(top), left: Math.round(left) });
  }, [open, box, i, step]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') setI((n) => Math.min(n + 1, live.length - 1));
      if (e.key === 'ArrowLeft') setI((n) => Math.max(n - 1, 0));
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose, live.length]);

  if (!open || !live.length) return null;
  const last = i === live.length - 1;

  return (
    <div className="tg-root" role="dialog" aria-modal="true" aria-label={t('tour.title')}>
      {/* The hole. Clicking the dimmed area leaves the tour — the same as pressing Escape, and the
          thing people try first. */}
      <button type="button" className="tg-scrim" aria-label={t('tour.skip')} onClick={onClose} />
      {!!box && <div className="tg-hole" style={box} />}
      <div className="tg-card" ref={cardRef} style={{ top: cardPos.top, left: cardPos.left }}>
        <div className="tg-count">{t('tour.step', { n: i + 1, of: live.length })}</div>
        <h4>{t(step.title)}</h4>
        <p>{t(step.body)}</p>
        <div className="tg-foot">
          <button type="button" className="tg-skip" onClick={onClose}>{t('tour.skip')}</button>
          <div className="tg-nav">
            {i > 0 && (
              <button type="button" className="tg-btn" onClick={() => setI(i - 1)}>{t('tour.back')}</button>
            )}
            <button
              type="button"
              className="tg-btn tg-next"
              onClick={() => (last ? (onFinish || onClose)() : setI(i + 1))}
            >
              {last ? t('tour.done') : t('tour.next')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
